<template>
    <div>
        <button type="button" class="btn btn--alpha" @click="handleUploadClick">
            {{ $t('DOCUMENT_WIZARD.UPLOAD_NEW_VERSION') }}
        </button>
        <input
            ref="filesRef"
            type="file"
            :accept="accept"
            class="hidden-dms"
            @change="handleChange"
        />
    </div>
</template>

<script>
import { first } from 'lodash-es'
import { mapActions, mapGetters } from 'vuex'
import {
    STEP_DOCUMENT_UPLOAD_OPTION,
    UPLOAD_NEW_VERSION_STATUSES
} from '@/constants'
import { toasts } from '@/mixins'
import { EventBus } from '@/EventBus'

export default {
    mixins: [toasts],

    computed: {
        ...mapGetters('company', ['company', 'regulationData'])
    },

    methods: {
        ...mapActions('document', ['uploadDocument']),
        ...mapActions('regulation', ['updateRegulationInputs']),

        handleUploadClick () {
            if (this.$refs.filesRef) {
                this.$refs.filesRef.click()
            }
        },

        async handleChange (event) {
            const document = first(event.target.files)

            document && (await this.handleUpload(document))
        },

        async handleUpload (document) {
            EventBus.$emit(
                'update-document-url',
                UPLOAD_NEW_VERSION_STATUSES.RESET
            )

            // Read outside the try on purpose (CT-594): both getters can be null, and that
            // would be a component defect rather than a handled API failure, so it must stay
            // visible to Vue's global error handler instead of reaching the catch below.
            const regulationId = this.regulationData.id
            const companyId = this.company.id
            const formData = this.getUploadFormData(document, regulationId)

            try {
                const file = await this.uploadDocument({ formData, companyId })

                if (this.stepId) {
                    await this.updateInputsForDocumentUpload(file, regulationId)
                }

                EventBus.$emit(
                    'update-document-url',
                    UPLOAD_NEW_VERSION_STATUSES.SET_NEW
                )
            } catch (error) {
                // Swallow only what the API layer has already surfaced (CT-594), and only in
                // the two shapes it actually produces: `uploadDocument` rejects with the
                // backend message string when the response carries one and with the Axios
                // error otherwise, and `updateRegulationInputs` rethrows the Axios error.
                // Both dispatch errors/handleError first, so the user has seen a toast or a
                // modal by the time we get here, and `handleUpload` is awaited only by
                // `handleChange` — a native @change handler whose rejection Vue would report
                // to Sentry, which is what made every expected 409 look like a crash.
                // Anything of another shape is a defect, not a handled failure, and must keep
                // travelling even if the store happened to toast a generic message for it: a
                // malformed success response, for instance, makes SET_DOCUMENT throw a plain
                // TypeError, and that belongs in Sentry rather than in a catch block.
                const isHandledApiFailure =
                    typeof error === 'string' || error?.isAxiosError === true

                if (!isHandledApiFailure) {
                    throw error
                }
            }
        },

        async updateInputsForDocumentUpload (file, regulationId) {
            let form = {}

            if (this.stepInputsNumber) {
                form[`step_document_upload_${this.stepInputsNumber}`] = { file }
                form[`step_own_document_${this.stepInputsNumber}`] =
                    STEP_DOCUMENT_UPLOAD_OPTION.USER_UPLOADED_DOCUMENT
            } else {
                form['step_document_upload_custom_step'] = { file }
            }

            const data = {
                payload: form,
                regulationId,
                stepId: this.stepId
            }

            await this.updateRegulationInputs(data)
        },

        getUploadFormData (document, regulationId) {
            const formData = new FormData()
            formData.append('document', document)
            formData.append('document_id', this.documentId)
            formData.append('regulation_id', regulationId)

            return formData
        }
    },

    props: {
        documentId: {
            type: Number,
            required: true
        },

        accept: {
            type: String,
            required: false,
            default: ''
        },

        stepId: {
            required: false
        },

        stepInputsNumber: {
            required: false
        }
    }
}
</script>

<style>
.hidden-dms {
    display: none;
}
</style>
